/**
 * Hook that exposes "is the field PWA installable / installed?" state and
 * the trigger to prompt for install on browsers that support it.
 *
 * Browser landscape:
 *   - Chrome/Edge/Android Chrome: fire `beforeinstallprompt`, we capture it
 *     and call .prompt() when the user taps install.
 *   - iOS Safari: never fires `beforeinstallprompt`. The user has to use
 *     Share → "Add to Home Screen". We detect iOS Safari and show
 *     instructional text instead of the button.
 *   - Already installed (display-mode: standalone): hide the prompt.
 *
 * Dismissal cooldown: if a user dismisses the prompt, snooze for 14 days via
 * localStorage so we don't nag them.
 */

import { useCallback, useEffect, useState } from 'react'

const DISMISS_KEY = 'lithos-field-install-dismissed-at'
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000 // 14 days

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

export type InstallSurface = 'native-prompt' | 'ios-instructions' | 'unsupported'

export interface InstallPromptState {
  /** True when the field PWA is already installed on this device. */
  isInstalled: boolean
  /** True when there's something useful to show (button or instructions). */
  canInstall: boolean
  /** What kind of UI to render. */
  surface: InstallSurface
  /** Trigger the native install prompt. No-op on iOS. */
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
  /** Hide the prompt for the cooldown period. */
  dismiss: () => void
}

/**
 * True when the page is running as an installed PWA (no browser chrome / no
 * address bar). Exported so the "Go to Field App" escape hatch (a stranded
 * standalone launch has no URL bar to type /field into) can reuse the single
 * source of truth rather than adding a third isStandalone copy.
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  // iOS Safari standalone flag.
  if ((window.navigator as { standalone?: boolean }).standalone) return true
  return false
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua)
  // Safari only — not Chrome on iOS, which uses CriOS.
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
  return isIOS && isSafari
}

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const ts = Number(raw)
    if (!Number.isFinite(ts)) return false
    return Date.now() - ts < DISMISS_COOLDOWN_MS
  } catch {
    return false
  }
}

export function useInstallPrompt(): InstallPromptState {
  const [installed, setInstalled] = useState<boolean>(() => isStandalone())
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [dismissed, setDismissed] = useState<boolean>(() => isDismissed())

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      // Stop the browser's mini-infobar so we can show our own UI.
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }
    function onAppInstalled() {
      setInstalled(true)
      setDeferredPrompt(null)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return 'unavailable' as const
    await deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    setDeferredPrompt(null)
    if (choice.outcome === 'dismissed') {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch {}
      setDismissed(true)
    }
    return choice.outcome
  }, [deferredPrompt])

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch {}
    setDismissed(true)
  }, [])

  let surface: InstallSurface = 'unsupported'
  if (deferredPrompt) surface = 'native-prompt'
  else if (isIosSafari() && !installed) surface = 'ios-instructions'

  const canInstall = !installed && !dismissed && surface !== 'unsupported'

  return {
    isInstalled: installed,
    canInstall,
    surface,
    promptInstall,
    dismiss,
  }
}
